import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, ModalDialog } from "@mui/joy";
import { DataGridPremium, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { getFetch } from "../../../../../fetch/fetch";
import totalLocationData from "../../../../../data/locationdata";

/**
 * 재원생 윈터 결제링크 발송하기 > 학생 선택(연필) → 재원생 고르기 팝업
 *  GET /dashboard/payments/managepayments/winterenrolledstudents (재원생, 결제선생 번호 = 결제선생 전송번호 > 학부모 > 학생)
 *  정렬: 지점(totalLocationData 순서) → 좌석번호 순. 오른쪽 위 이름 검색. 아래에 '강남점(3번) 고용범, …' 선택 목록.
 *  확인 → props.onConfirm(선택 학생 배열, 지점·좌석 순)
 */

export interface EnrolledStudent {
    id: number;
    name: string;
    location: string; // 한글 (studentLocation)
    seat: any;
    phoneNumber: string;
    parentPhoneNumber: string;
    payteacherPhoneNumber: string;
    sendPhone: string;
}

//표시용 '강남점(3번) 고용범'
export const studentLabel = (st: EnrolledStudent) => `${st.location || "지점 없음"}${st.seat ? `(${st.seat}번)` : ""} ${st.name}`;

const fmtTel = (v: string) => {
    const d = String(v || "").replace(/\D/g, "");
    return d.length === 11 ? d.slice(0, 3) + "-" + d.slice(3, 7) + "-" + d.slice(7) : d;
};

//지점 순서(totalLocationData) → 좌석 숫자 순
const locationOrder = (korean: string) => {
    const i = totalLocationData.findIndex((each: any) => each.korean === korean);
    return i < 0 ? 9999 : i;
};
export const sortStudents = (list: EnrolledStudent[]) => list.slice().sort((a, b) => {
    const la = locationOrder(a.location), lb = locationOrder(b.location);
    if (la !== lb) return la - lb;
    if (a.location !== b.location) return (a.location || "").localeCompare(b.location || "");
    const sa = Number(a.seat), sb = Number(b.seat);
    const na = Number.isFinite(sa) && a.seat !== null && a.seat !== "" ? sa : 99999;
    const nb = Number.isFinite(sb) && b.seat !== null && b.seat !== "" ? sb : 99999;
    if (na !== nb) return na - nb;
    return (a.name || "").localeCompare(b.name || "");
});

const columns = [
    { field: "location", headerName: "지점", width: 110 },
    { field: "name", headerName: "이름", width: 120 },
    { field: "seat", headerName: "좌석번호", width: 90 },
    { field: "sendPhoneText", headerName: "결제선생 전송번호", width: 170 },
];

const WinterEnrolledPickerModal = (props: { open: boolean; selected: EnrolledStudent[]; onCancel: () => void; onConfirm: (list: EnrolledStudent[]) => void }) => {

    const [students, setStudents] = useState<EnrolledStudent[]>([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");
    const [selection, setSelection] = useState<GridRowSelectionModel>([]);

    //열 때마다 목록 새로 받고, 이미 고른 학생은 체크된 채로
    useEffect(() => {
        if (!props.open) return;
        setQuery("");
        setSelection(props.selected.map(x => x.id));
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                const result: any = await getFetch("/dashboard/payments/managepayments/winterenrolledstudents");
                if (!alive) return;
                if (result && result.message === "success" && Array.isArray(result.students)) {
                    setStudents(sortStudents(result.students));
                }
            } catch (e) {
                console.log(e);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [props.open]);

    const rows = useMemo(() => {
        const q = query.trim();
        return students
            .filter(st => !q || (st.name || "").includes(q))
            .map(st => ({ ...st, seat: st.seat === null || st.seat === undefined ? "" : st.seat, sendPhoneText: fmtTel(st.sendPhone) || "번호 없음" }));
    }, [students, query]);

    //선택 목록 (지점·좌석 순)
    const selectedList = useMemo(() => {
        const ids = new Set(selection.map(v => +v));
        return students.filter(st => ids.has(st.id));
    }, [students, selection]);

    return (
        <Modal open={props.open} onClose={props.onCancel} sx={{ zIndex: 1400 }}>
            <ModalDialog sx={{ width: 640, maxWidth: "calc(100vw - 32px)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 12 }}>
                    <div style={{ fontFamily: "Apple_SB", fontSize: 20 }}>재원생 선택</div>
                    <Input
                        size="sm"
                        placeholder="이름 검색"
                        value={query}
                        onChange={(e: any) => setQuery(e.target.value)}
                        sx={{ width: 200 }}
                    />
                </div>
                <div style={{ height: 460, width: "100%", backgroundColor: "white" }}>
                    <DataGridPremium
                        rows={rows}
                        columns={columns}
                        density="compact"
                        loading={loading}
                        checkboxSelection
                        disableRowSelectionOnClick={false}
                        keepNonExistentRowsSelected
                        rowSelectionModel={selection}
                        onRowSelectionModelChange={(model: GridRowSelectionModel) => setSelection(model)}
                        hideFooter
                    />
                </div>
                <div style={{ marginTop: 12, fontSize: 14, color: "#4E5968", lineHeight: 1.6, maxHeight: 96, overflowY: "auto" }}>
                    {selectedList.length === 0
                        ? "선택된 학생이 없어요"
                        : <><span style={{ fontWeight: 600, color: "#191F28" }}>{selectedList.length}명 · </span>{selectedList.map(studentLabel).join(", ")}</>
                    }
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                    <Button variant="plain" color="neutral" onClick={props.onCancel}>취소</Button>
                    <Button variant="solid" onClick={() => props.onConfirm(selectedList)}>확인</Button>
                </div>
            </ModalDialog>
        </Modal>
    );
};

export default WinterEnrolledPickerModal;
